import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

interface ToastItem {
  id: number
  message: string
}

interface ToastApi {
  showError: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return ctx
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])

  const showError = useCallback(
    (message: string) => {
      const id = (nextId.current += 1)
      setToasts((t) => [...t, { id, message }])
      setTimeout(() => dismiss(id), 5000)
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(() => ({ showError }), [showError])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-viewport" role="alert" aria-live="assertive">
        {toasts.map((t) => (
          <div key={t.id} className="toast toast-error">
            <span>{t.message}</span>
            <button onClick={() => dismiss(t.id)} aria-label="Dismiss">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
